<template>
  <div class="risk-control-panel" v-loading="loading">
    <section class="panel-header">
      <div>
        <h3>Ford 短时风控配置</h3>
        <p>集中维护短时窗口与共享地区阈值，配置保存后以服务端回读值为准。</p>
      </div>

      <el-button
        :icon="Refresh"
        :loading="loading"
        :disabled="busy"
        plain
        @click="fetchConfig"
      >
        刷新
      </el-button>
    </section>

    <el-alert
      v-if="apiError"
      class="api-error"
      type="error"
      title="后端请求失败"
      :description="apiError"
      :closable="false"
      show-icon
    />

    <section class="config-section enable-section">
      <div class="section-heading">
        <div>
          <h4>总开关</h4>
          <p>FordServer:RiskControl:Enable</p>
        </div>
        <el-tag :type="current.enabled ? 'danger' : 'info'" effect="plain">
          {{ current.enabled ? '已开启' : '已关闭' }}
        </el-tag>
      </div>

      <div class="enable-control">
        <el-switch
          :model-value="current.enabled"
          inline-prompt
          active-text="开启"
          inactive-text="关闭"
          :loading="savingEnabled"
          :disabled="busy"
          @change="handleEnabledChange"
        />
        <div>
          <strong>{{ current.enabled ? '达到任一共享阈值时触发风控' : '当前不会按短时阈值触发风控' }}</strong>
          <p>开启会立即影响线上判断，系统会在调用现有 enabled API 前要求确认。</p>
        </div>
      </div>

      <div class="risk-note" :class="{ warning: current.enabled }">
        <el-icon><WarningFilled /></el-icon>
        <span>开启后，降低任一地区共享阈值会让风控更容易触发。</span>
      </div>
    </section>

    <section class="config-section">
      <div class="section-heading">
        <div>
          <h4>窗口与地区共享阈值</h4>
          <p>窗口单位为秒；两个访问方向共用同一组北美/非北美阈值。</p>
        </div>
        <el-tag effect="plain">四项业务参数</el-tag>
      </div>

      <el-form label-position="top" class="parameter-form">
        <div class="parameter-grid">
          <el-form-item label="同一 SN 切换 VIN 窗口">
            <el-input-number
              v-model.number="draft.snVinWindowSeconds"
              :min="RAPID_SWITCH_LIMITS.windowSeconds.min"
              :max="RAPID_SWITCH_LIMITS.windowSeconds.max"
              :step="1"
              step-strictly
              controls-position="right"
              :disabled="busy"
            />
            <div class="value-hints">
              <span>当前值：{{ current.snVinWindowSeconds }} 秒</span>
              <span>默认值：{{ current.defaults.snVinWindowSeconds }} 秒</span>
            </div>
          </el-form-item>

          <el-form-item label="同一 VIN 被多个 SN 访问窗口">
            <el-input-number
              v-model.number="draft.vinSnWindowSeconds"
              :min="RAPID_SWITCH_LIMITS.windowSeconds.min"
              :max="RAPID_SWITCH_LIMITS.windowSeconds.max"
              :step="1"
              step-strictly
              controls-position="right"
              :disabled="busy"
            />
            <div class="value-hints">
              <span>当前值：{{ current.vinSnWindowSeconds }} 秒</span>
              <span>默认值：{{ current.defaults.vinSnWindowSeconds }} 秒</span>
            </div>
          </el-form-item>

          <el-form-item label="北美共享阈值">
            <el-input-number
              v-model.number="draft.northAmericaThreshold"
              :min="RAPID_SWITCH_LIMITS.threshold.min"
              :max="RAPID_SWITCH_LIMITS.threshold.max"
              :step="1"
              step-strictly
              controls-position="right"
              :disabled="busy"
            />
            <div class="value-hints">
              <span>当前值：{{ current.northAmericaThreshold }}</span>
              <span>默认值：{{ current.defaults.northAmericaThreshold }}</span>
            </div>
          </el-form-item>

          <el-form-item label="非北美共享阈值">
            <el-input-number
              v-model.number="draft.otherThreshold"
              :min="RAPID_SWITCH_LIMITS.threshold.min"
              :max="RAPID_SWITCH_LIMITS.threshold.max"
              :step="1"
              step-strictly
              controls-position="right"
              :disabled="busy"
            />
            <div class="value-hints">
              <span>当前值：{{ current.otherThreshold }}</span>
              <span>默认值：{{ current.defaults.otherThreshold }}</span>
            </div>
          </el-form-item>
        </div>
      </el-form>

      <div class="parameter-note">
        <el-icon><InfoFilled /></el-icon>
        <span>
          窗口范围 {{ RAPID_SWITCH_LIMITS.windowSeconds.min }}–{{ RAPID_SWITCH_LIMITS.windowSeconds.max }} 秒，
          阈值范围 {{ RAPID_SWITCH_LIMITS.threshold.min }}–{{ RAPID_SWITCH_LIMITS.threshold.max }}；
          北美共享阈值不得小于非北美共享阈值。
        </span>
      </div>

      <div class="section-actions">
        <el-button :disabled="busy" @click="fetchConfig">刷新</el-button>
        <el-button :disabled="busy" @click="handleRestoreDefaults">恢复默认值</el-button>
        <el-button
          type="primary"
          :loading="savingConfig"
          :disabled="busy || !hasConfigChanges"
          @click="handleSaveConfig"
        >
          保存参数
        </el-button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { InfoFilled, Refresh, WarningFilled } from '@element-plus/icons-vue';

import {
  getFordRiskControlConfig,
  saveFordRiskControlConfig,
  setFordRiskControlEnabled,
} from '@/api/users';
import {
  RAPID_SWITCH_DEFAULTS,
  RAPID_SWITCH_LIMITS,
  isRiskierChange,
  normalizeRiskControlPayload,
  toRiskControlRequest,
  validateRiskControlDraft,
} from '@/utils/fordRiskControlUi.mjs';

const current = reactive({
  enabled: false,
  ...RAPID_SWITCH_DEFAULTS,
  defaults: { ...RAPID_SWITCH_DEFAULTS },
});
const draft = reactive({ ...RAPID_SWITCH_DEFAULTS });

const loading = ref(false);
const savingConfig = ref(false);
const savingEnabled = ref(false);
const apiError = ref('');

const busy = computed(() => (
  loading.value || savingConfig.value || savingEnabled.value
));

const hasConfigChanges = computed(() => {
  const request = toRiskControlRequest(draft);
  return Object.entries(request).some(([field, value]) => current[field] !== value);
});

function applyServerConfig(config) {
  current.enabled = config.enabled;
  current.snVinWindowSeconds = config.snVinWindowSeconds;
  current.vinSnWindowSeconds = config.vinSnWindowSeconds;
  current.northAmericaThreshold = config.northAmericaThreshold;
  current.otherThreshold = config.otherThreshold;
  current.defaults = { ...config.defaults };
  Object.assign(draft, toRiskControlRequest(config));
}

function readableApiError(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error ||
    (typeof error.response?.data === 'string' ? error.response.data : '') ||
    error.message ||
    fallback
  );
}

function showApiError(error, fallback) {
  apiError.value = readableApiError(error, fallback);
  ElMessage.error(`${fallback}：${apiError.value}`);
}

async function fetchConfig() {
  loading.value = true;
  apiError.value = '';
  try {
    const response = await getFordRiskControlConfig();
    applyServerConfig(normalizeRiskControlPayload(response.data));
    return true;
  } catch (error) {
    showApiError(error, '获取短时风控配置失败');
    return false;
  } finally {
    loading.value = false;
  }
}

async function confirmEnable() {
  try {
    await ElMessageBox.confirm(
      '开启后，短时窗口达到共享地区阈值会立即触发线上风控。确认开启？',
      '开启短时风控',
      {
        confirmButtonText: '确认开启',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    return true;
  } catch (error) {
    return false;
  }
}

async function confirmRiskierChange(before, after) {
  if (!isRiskierChange(before, after)) {
    return true;
  }

  try {
    await ElMessageBox.confirm(
      '检测到地区共享阈值降低，风控将更容易触发。确认保存？',
      '确认降低阈值',
      {
        confirmButtonText: '确认保存',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    return true;
  } catch (error) {
    return false;
  }
}

async function handleEnabledChange(nextEnabled) {
  if (nextEnabled === current.enabled) {
    return;
  }
  if (nextEnabled && !(await confirmEnable())) {
    return;
  }

  savingEnabled.value = true;
  apiError.value = '';
  try {
    await setFordRiskControlEnabled(nextEnabled);
    current.enabled = nextEnabled;
    ElMessage.success(nextEnabled ? '短时风控已开启' : '短时风控已关闭');
  } catch (error) {
    showApiError(error, '保存风控总开关失败');
  } finally {
    savingEnabled.value = false;
  }
}

async function saveAndRefresh(request, successMessage) {
  savingConfig.value = true;
  apiError.value = '';
  try {
    await saveFordRiskControlConfig(request);
    const refreshed = await fetchConfig();
    if (refreshed) {
      ElMessage.success(successMessage);
    }
  } catch (error) {
    showApiError(error, '保存短时风控参数失败');
  } finally {
    savingConfig.value = false;
  }
}

async function handleSaveConfig() {
  const validationError = validateRiskControlDraft(draft);
  if (validationError) {
    ElMessage.warning(validationError);
    return;
  }

  const request = toRiskControlRequest(draft);
  if (!(await confirmRiskierChange(toRiskControlRequest(current), request))) {
    return;
  }
  await saveAndRefresh(request, '短时风控参数已保存');
}

async function handleRestoreDefaults() {
  const request = toRiskControlRequest(current.defaults);
  const validationError = validateRiskControlDraft(request);
  if (validationError) {
    ElMessage.error(`服务端默认值不可用：${validationError}`);
    return;
  }
  if (!(await confirmRiskierChange(toRiskControlRequest(current), request))) {
    return;
  }
  await saveAndRefresh(request, '已恢复服务端默认值');
}

onMounted(() => {
  fetchConfig();
});
</script>

<style scoped>
.risk-control-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p,
.enable-control p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.5;
}

.api-error {
  align-items: flex-start;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h4 {
  font-size: 16px;
}

.enable-control {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #132033;
}

.enable-control strong {
  font-size: 14px;
}

.risk-note,
.parameter-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  color: #0f766e;
  background: #ecfdf5;
  font-size: 13px;
  line-height: 1.5;
}

.risk-note.warning {
  border-color: #fed7aa;
  color: #915a00;
  background: #fff7ed;
}

.parameter-note {
  border-color: #d8e9fb;
  color: #416174;
  background: #f2f8ff;
}

.parameter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 18px;
}

.parameter-grid :deep(.el-form-item) {
  min-width: 0;
  margin-bottom: 16px;
}

.parameter-grid :deep(.el-form-item__label) {
  color: #334155;
  font-weight: 800;
}

.parameter-grid :deep(.el-input-number) {
  width: 100%;
}

.value-hints {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  color: #7a8a9d;
  font-size: 12px;
  line-height: 1.4;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

@media (max-width: 720px) {
  .panel-header,
  .section-heading,
  .enable-control {
    align-items: flex-start;
    flex-direction: column;
  }

  .parameter-grid {
    grid-template-columns: 1fr;
  }

  .section-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
